import {Dictionary, convertAllErrorsFromBeToFormError, putAll, takeLatestF, validateSchema} from '@fl/cmsch-fe-library';
import {isUndefined, keys, omitBy} from 'lodash/fp';
import {SagaIterator} from 'redux-saga';
import {isEmpty, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {ValidateCreateOtherSubjectBindingAdmin} from 'api/gen/ValidateCreateOtherSubjectBindingAdmin';
import {ValidateOtherSubjectBindingManager} from 'api/gen/ValidateOtherSubjectBindingManager';
import {t} from 'app/translations';
import {canRunAsyncValidation, formHelpers} from 'utils/forms';
import {showBeError} from 'utils/show-be-error';

import {ValidateOtherSubjectErrorsSchema} from '../../types/errors/validate-other-subject-errors';
import {
    managementOtherSubjectFormName,
    ManagementOtherSubjectFormTag,
} from '../../types/management-other-subject-form-values';
import {SubjectManagementFormValuesErrors} from '../../types/subject-management-form-values';
import {ValidateManagementOtherSubjectAction} from '../action';

interface ApiTypes {
    otherSubjectId: number;
    roleIds: Array<number> | null;
    userProfileId: number;
    approvedFrom: string;
    approvedTo: string | null;
    tag: ManagementOtherSubjectFormTag;
    userProfileRoleIds: Array<number>;
}
const getApiValues = ({
    otherSubjectId,
    roleIds,
    userProfileId,
    approvedFrom,
    approvedTo,
    userProfileRoleIds,
    tag,
}: ApiTypes): ValidateOtherSubjectBindingManager => tag === 'ValidateCreateOtherSubjectBindingManager'
    ? ({
        tag,
        binding: {
            approvedFrom,
            approvedTo,
            otherSubjectId,
            roleIds: opt(roleIds).orCrash('No roleIds found'),
            userProfileId,
        },
    })
    : {
        tag,
        binding: {
            approvedTo,
            userProfileOtherSubjectRoleId: opt(userProfileRoleIds).headIn().orCrash('no userProfileRoleIds found'),
        },
    };

const mapErrors = (
    errors: Dictionary<keyof ValidateCreateOtherSubjectBindingAdmin, string>,
): SubjectManagementFormValuesErrors => {
    const validErrors = validateSchema(ValidateOtherSubjectErrorsSchema, 'FE')(errors).binding;

    return omitBy(isUndefined)<SubjectManagementFormValuesErrors>({
        approvedFrom: validErrors.approvedFrom,
        approvedTo: validErrors.approvedTo,
        subjectId: validErrors.otherSubjectId,
        userProfileId: validErrors.otherSubjectId,
        roleIds: validErrors.roleIds,
    });
};

// eslint-disable-next-line max-lines-per-function
function* execute({payload: {source}}: ValidateManagementOtherSubjectAction): SagaIterator {
    const {
        approvedFrom,
        approvedTo,
        subjectId,
        roleIds,
        userProfileId,
        tag,
        userProfileRoleIds,
    } = (
        yield* select(formHelpers.formValues(managementOtherSubjectFormName))
    ).orCrash(`no ${managementOtherSubjectFormName} form values`);

    const pristine = yield* select(formHelpers.isPristine(managementOtherSubjectFormName));

    const syncErrors = yield* select(formHelpers.getSyncErrors(managementOtherSubjectFormName));
    if (syncErrors.exists(errors => !canRunAsyncValidation(errors, source))) return;

    if (
        approvedFrom === null
        || subjectId === null
        || userProfileId === null
        || isEmpty(roleIds)
        || pristine
        || (approvedTo !== null && approvedFrom > approvedTo)
    ) return; // synchronously not valid

    yield* put(formHelpers.startAsyncValidation(managementOtherSubjectFormName));

    const response = yield* call(
        Api.validateUserProfileOtherSubjectRoleBySubjectManager,
        getApiValues({
            otherSubjectId: subjectId,
            roleIds,
            userProfileId,
            approvedFrom,
            approvedTo,
            tag: opt(tag).orCrash('Tag does not exist'),
            userProfileRoleIds: opt(userProfileRoleIds).orElse([]),
        }),
    );

    if (!response.isSuccess) {
        if (response.isBadRequest) {
            const errors = convertAllErrorsFromBeToFormError(response.data);
            const otherSubjectErrors = mapErrors(errors.errors);
            yield* put(formHelpers.stopAsyncValidation(managementOtherSubjectFormName, otherSubjectErrors));
            const fieldsWithNewErrors = keys(otherSubjectErrors) as Array<keyof typeof otherSubjectErrors>;

            if (!isEmpty(fieldsWithNewErrors)) {
                yield* put(formHelpers.touch(managementOtherSubjectFormName, ...fieldsWithNewErrors));
            }
        } else {
            yield putAll(showBeError(response, t('common')('validateEntry')));
        }
    } else {
        yield* put(formHelpers.stopAsyncValidation(managementOtherSubjectFormName, {}));
    }
}

/** Runs the admin saga that validates other-subject management form values before save. */
export function* validateManagementOtherSubjectSaga(): SagaIterator {
    yield takeLatestF('admin/VALIDATE_MANAGEMENT_OTHER_SUBJECT', execute);
}
